/**
 * @fileoverview Promotional Cover Screen Component for Tug Of War on stadium LED screens (/screen).
 * Renders high-impact promotional branding with sponsor/club logos, floating title animations,
 * dynamic theme backgrounds (color, image, video), and contrast-safe typography.
 * Implements a Zero-Canvas architecture with GPU Layer Compositing (0% CPU usage in idle state).
 * 
 * @module components/Screen/Sections/TugOfWar/TugOfWarCover
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { GetConfig, GetTheme } from '../../../../utilities/BL/TugOfWar';
import {
    resolveAssetUrl,
    DEFAULT_COVER_BG_SVG,
    DEFAULT_ROPE_SVG,
    DEFAULT_ROPE_FLAG_SVG,
    DEFAULT_PLAYER1_PHASE1_SVG,
    DEFAULT_PLAYER2_PHASE1_SVG
} from './TugOfWarAssets';

/**
 * Default fallback colors and configuration when theme is not provided.
 * @constant {Object}
 */
const DEFAULT_COVER_CONFIGURATION = {
    coverTitle: 'ESTIRA Y AFLOJA',
    coverBackgroundType: 'color',
    coverBackgroundColor: '#0b0f19',
    coverBackgroundUrl: '',
    coverTextColor: '#ffffff',
    fontFamily: 'Montserrat',
    brandLogoUrl: '',
    sponsorLogoUrl: '',
    player1Name: 'EQUIPO 1',
    player2Name: 'EQUIPO 2',
    player1Color: '#00f0ff',
    player2Color: '#ff0055'
};

/**
 * TugOfWarCover Component (/screen - Componente 47)
 * 
 * @function TugOfWarCover
 * @param {Object} props - Component properties.
 * @param {Object} [props.payload] - WebSocket payload or initial data from parent Screen router.
 * @param {Object} [props.theme] - Theme object directly passed from Screen.
 * @param {Object} [props.config] - Config object directly passed from Screen.
 * @param {string} [props.roomId] - Room GUID.
 * @returns {React.ReactElement} The rendered stadium cover view.
 */
export const TugOfWarCover = ({ payload, theme: propTheme, config: propConfig, roomId }) => {
    // =========================================================================
    // 1. ESTADO DEL TEMA VISUAL ACTIVO
    // =========================================================================
    const [activeThemeData, setActiveThemeData] = useState(null);

    /**
     * Fetches the active theme from the backend API if not already present in the incoming payload.
     * 
     * @async
     * @function fetchActiveThemeConfiguration
     */
    const fetchActiveThemeConfiguration = useCallback(async () => {
        try {
            const configurationResponse = await GetConfig(roomId);
            if (configurationResponse && configurationResponse.success && configurationResponse.data) {
                const roomConfiguration = configurationResponse.data;
                if (roomConfiguration.activeTheme) {
                    setActiveThemeData(roomConfiguration.activeTheme);
                } else if (roomConfiguration.activeThemeId) {
                    const themeResponse = await GetTheme(roomConfiguration.activeThemeId, roomId);
                    if (themeResponse && themeResponse.success && themeResponse.data) {
                        setActiveThemeData(themeResponse.data);
                    }
                }
            }
        } catch (themeFetchError) {
            // Non-blocking fallback to default configuration
        }
    }, [roomId]);

    // Synchronize theme when payload changes or trigger initial fetch
    useEffect(() => {
        if (propTheme) {
            const directTheme = propTheme.theme && typeof propTheme.theme === 'object' ? propTheme.theme : propTheme;
            setActiveThemeData(directTheme);
            return;
        }

        if (propConfig) {
            const confTheme = propConfig.theme || propConfig.activeTheme;
            if (confTheme) {
                setActiveThemeData(confTheme);
                return;
            }
        }

        if (payload) {
            const incomingThemeData = payload.state?.currentTheme ||
                payload.data?.currentTheme ||
                payload.payload?.currentTheme ||
                payload.theme ||
                payload.data?.theme;

            if (incomingThemeData) {
                setActiveThemeData(incomingThemeData);
                return;
            }
        }

        if (!activeThemeData) {
            fetchActiveThemeConfiguration();
        }
    }, [payload, propTheme, propConfig, activeThemeData, fetchActiveThemeConfiguration]);

    // =========================================================================
    // 2. EXTRACCIÓN DE ATRIBUTOS Y FALLBACKS
    // =========================================================================
    const coverBackgroundType = activeThemeData?.coverBackgroundType || DEFAULT_COVER_CONFIGURATION.coverBackgroundType;
    const coverBackgroundColor = activeThemeData?.coverBackgroundColor || DEFAULT_COVER_CONFIGURATION.coverBackgroundColor;
    const coverBackgroundUrl = resolveAssetUrl(
        activeThemeData?.coverBackgroundUrl,
        coverBackgroundType === 'image' ? DEFAULT_COVER_BG_SVG : ''
    );
    const coverTextColor = activeThemeData?.coverTextColor || DEFAULT_COVER_CONFIGURATION.coverTextColor;
    const coverTitleText = activeThemeData?.coverTitle || DEFAULT_COVER_CONFIGURATION.coverTitle;
    const fontFamily = activeThemeData?.fontFamily || DEFAULT_COVER_CONFIGURATION.fontFamily;
    const brandLogoUrl = resolveAssetUrl(activeThemeData?.brandLogoUrl);
    const sponsorLogoUrl = resolveAssetUrl(activeThemeData?.sponsorLogoUrl);

    const player1Name = activeThemeData?.player1Name || DEFAULT_COVER_CONFIGURATION.player1Name;
    const player2Name = activeThemeData?.player2Name || DEFAULT_COVER_CONFIGURATION.player2Name;
    const player1Color = activeThemeData?.player1Color || DEFAULT_COVER_CONFIGURATION.player1Color;
    const player2Color = activeThemeData?.player2Color || DEFAULT_COVER_CONFIGURATION.player2Color;

    const p1Phase1Avatar = resolveAssetUrl(activeThemeData?.player1Phase1Avatar, DEFAULT_PLAYER1_PHASE1_SVG);
    const p2Phase1Avatar = resolveAssetUrl(activeThemeData?.player2Phase1Avatar, DEFAULT_PLAYER2_PHASE1_SVG);
    const ropeTextureUrl = resolveAssetUrl(activeThemeData?.ropeTextureUrl, DEFAULT_ROPE_SVG);
    const ropeFlagUrl = resolveAssetUrl(activeThemeData?.ropeFlagUrl, DEFAULT_ROPE_FLAG_SVG);

    // =========================================================================
    // 3. RENDERIZADO DEL FONDO DINÁMICO (ZERO-CANVAS GPU COMPOSITED)
    // =========================================================================
    const renderDynamicBackgroundLayer = () => {
        if (coverBackgroundType === 'video' && coverBackgroundUrl) {
            return (
                <video
                    src={coverBackgroundUrl}
                    className="tugofwar-cover__background-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            );
        }

        if (coverBackgroundType === 'image' && coverBackgroundUrl) {
            return (
                <div
                    className="tugofwar-cover__background-image"
                    style={{ backgroundImage: `url(${coverBackgroundUrl})` }}
                />
            );
        }

        return (
            <div
                className="tugofwar-cover__background-color"
                style={{
                    backgroundColor: coverBackgroundColor,
                    backgroundImage: `radial-gradient(circle at 50% 40%, rgba(30, 27, 75, 0.8) 0%, rgba(2, 6, 23, 0.95) 100%)`
                }}
            />
        );
    };

    return (
        <div
            className="tugofwar-screen tugofwar-cover"
            style={{ fontFamily: `${fontFamily}, sans-serif` }}
        >
            {/* Capa 1: Fondo Dinámico GPU */}
            {renderDynamicBackgroundLayer()}

            {/* Capa 2: Efectos de Iluminación y Haces de Luz */}
            {/* <div className="tugofwar-cover__light-beam-left" />
            <div className="tugofwar-cover__light-beam-right" /> */}

            {/* Capa 3: Header Superior con Logos */}
            {/* <header className="tugofwar-cover__header">
                {brandLogoUrl && (
                    <div className="tugofwar-cover__brand-container">
                        <img
                            src={brandLogoUrl}
                            alt="Brand Logo"
                            className="tugofwar-cover__brand-logo"
                        />
                    </div>
                )}
                {sponsorLogoUrl && (
                    <div className="tugofwar-cover__sponsor-container">
                        <span className="tugofwar-cover__sponsor-label">PATROCINADO POR</span>
                        <img
                            src={sponsorLogoUrl}
                            alt="Sponsor Logo"
                            className="tugofwar-cover__sponsor-logo"
                        />
                    </div>
                )}
            </header> */}

            {/* Capa 4: Contenido Central Hero */}
            <main className="tugofwar-cover__content">
                <div className="tugofwar-cover__title-wrapper">
                    <img src='/img/page/adsl-five.png' alt="Logo Dorados" style={{ width: '450px' }} />
                    <p style={{ fontSize: '2.2em', marginTop: '0.8em' }}>PRESENTA</p>
                    <h1
                        className="tugofwar-cover__title"
                        style={{ color: coverTextColor }}
                    >
                        {coverTitleText}
                    </h1>
                    {/* <div className="tugofwar-cover__subtitle-badge">
                        ¡ESTIRA CON FUERZA POR TU EQUIPO!
                    </div> */}
                </div>

                {/* Capa 5: Showdown Preview de Equipos y Cuerda */}
                {/* <div className="tugofwar-cover__showdown"> */}
                    {/* Equipo 1 */}
                    {/* <div className="tugofwar-cover__team tugofwar-cover__team--left">
                        <div
                            className="tugofwar-cover__team-name"
                            style={{ color: player1Color, textShadow: `0 0 20px ${player1Color}88` }}
                        >
                            {player1Name}
                        </div>
                        <div className="tugofwar-cover__avatar-wrap">
                            <img
                                src={p1Phase1Avatar}
                                alt={player1Name}
                                className="tugofwar-cover__avatar"
                            />
                        </div>
                    </div> */}

                    {/* Cuerda Central con Pañuelo y VS */}
                    {/* <div className="tugofwar-cover__rope-preview">
                        <div
                            className="tugofwar-cover__rope-line"
                            style={{ backgroundImage: `url(${ropeTextureUrl})` }}
                        >
                            <img
                                src={ropeFlagUrl}
                                alt="Marcador central"
                                className="tugofwar-cover__rope-flag"
                            />
                        </div>
                        <div className="tugofwar-cover__vs-badge">VS</div>
                    </div> */}

                    {/* Equipo 2 */}
                    {/* <div className="tugofwar-cover__team tugofwar-cover__team--right">
                        <div
                            className="tugofwar-cover__team-name"
                            style={{ color: player2Color, textShadow: `0 0 20px ${player2Color}88` }}
                        >
                            {player2Name}
                        </div>
                        <div className="tugofwar-cover__avatar-wrap">
                            <img
                                src={p2Phase1Avatar}
                                alt={player2Name}
                                className="tugofwar-cover__avatar"
                            />
                        </div>
                    </div> */}
                {/* </div> */}
            </main>
        </div>
    );
};

TugOfWarCover.propTypes = {
    payload: PropTypes.shape({
        state: PropTypes.object,
        data: PropTypes.object,
        payload: PropTypes.object,
        theme: PropTypes.object
    }),
    theme: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    config: PropTypes.object,
    roomId: PropTypes.string
};

TugOfWarCover.defaultProps = {
    payload: null,
    theme: null,
    config: null,
    roomId: null
};

export default TugOfWarCover;
